/**
 * Keeps the full in-app URL across a centralized-login hand-off.
 *
 * auth-bridge's redirect_uri is origin + pathname, so the portal hands the user
 * back with only `?code&state` — deep links (`/agents?conversationId=…`, links
 * moved from connect.* to app.* on 2026-10-07, notification links) lost their
 * query. The guard remembers the URL a sign-in attempt started on and, after
 * the code exchange, redirects to it. Per tab (sessionStorage), because the
 * hand-off returns to the same tab. The FIRST URL of an attempt wins for
 * FRESH_MS: a second guarded navigation (e.g. a bare `/agents` fired while the
 * first was awaiting) must not overwrite the deep link. A navigation that finds
 * a session clears the entry (forgetReturnUrl).
 */
const KEY = 'crewdle:login-return-url';
const FRESH_MS = 2 * 60 * 1000;

interface Entry {
  url: string;
  at: number;
}

function read(storage: ReturnUrlStorage | null): Entry | null {
  try {
    const raw = storage?.getItem(KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Entry;
    return typeof entry?.url === 'string' && typeof entry.at === 'number' ? entry : null;
  } catch {
    return null;
  }
}

export interface ReturnUrlStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** True when this navigation is the portal's callback (`?code`). */
export function isLoginCallback(url: string): boolean {
  return new URL(url, 'https://x.invalid').searchParams.has('code');
}

/** Remember a guarded, non-callback in-app URL (first of an attempt wins). */
export function rememberReturnUrl(storage: ReturnUrlStorage | null, url: string, now = Date.now()): void {
  if (!storage || isLoginCallback(url)) return;
  const existing = read(storage);
  if (existing && now - existing.at < FRESH_MS) return;
  try {
    storage.setItem(KEY, JSON.stringify({ url, at: now } satisfies Entry));
  } catch { /* storage full / disabled — the callback falls back to its own path */ }
}

/** A navigation found a session: no hand-off is pending. */
export function forgetReturnUrl(storage: ReturnUrlStorage | null): void {
  try {
    storage?.removeItem(KEY);
  } catch { /* ignore */ }
}

/**
 * The URL to land on after the callback: the remembered one when it is on the
 * same path, else the callback URL without `code`/`state`. Consumes the entry.
 */
export function takeReturnUrl(storage: ReturnUrlStorage | null, callbackUrl: string): string {
  const callback = new URL(callbackUrl, 'https://x.invalid');
  callback.searchParams.delete('code');
  callback.searchParams.delete('state');
  const fallback = `${callback.pathname}${callback.search}${callback.hash}`;
  const stored = read(storage)?.url ?? null;
  forgetReturnUrl(storage);
  if (!stored || !stored.startsWith('/') || stored.startsWith('//')) return fallback;
  // redirect_uri is origin + pathname: an entry for another path is stale.
  return new URL(stored, 'https://x.invalid').pathname === callback.pathname ? stored : fallback;
}
